import React from "react"
import styled from "styled-components"
import { rem } from "polished"

import { media } from "@sr/common"
import {
  RegBody,
  SanityImg,
  SemiBdTitle3,
  SemiBdTitle4,
  SemiBdTitle5,
} from "@sr/gatsby-theme-ui"
import SanityImage from "gatsby-plugin-sanity-image"

export type HardwareCategoryEducationalInfoContentSection = {
  label: string
  title: string
  text: string
}

export interface HardwareCategoryEducationalInfoPropsShape {
  educationalInfo: {
    title: string
    text: string
    img: SanityImg
    contentSections: HardwareCategoryEducationalInfoContentSection[]
  }
}

const HardwareCategoryEducationalInfo = ({
  educationalInfo,
}: HardwareCategoryEducationalInfoPropsShape) => {
  return (
    <HardwareCategoryEducationalInfoStyled>
      <div className="hardware-category-educational-info__container">
        {educationalInfo?.img?.asset && (
          <SanityImage
            asset={educationalInfo?.img?.asset}
            alt={educationalInfo?.img?.alt || ""}
            className="hardware-category-educational-info__img"
            crop={educationalInfo?.img?.crop}
            hotspot={educationalInfo?.img?.hotspot}
          />
        )}
        <SemiBdTitle3 className="hardware-category-educational-info__title">
          {educationalInfo?.title}
        </SemiBdTitle3>
        <RegBody className="hardware-category-educational-info__text">
          {educationalInfo?.text}
        </RegBody>
        <div className="hardware-category-educational-info__content-container">
          {educationalInfo?.contentSections?.map((section, i) => {
            return (
              <div
                className="hardware-category-educational-info__content-inner-container"
                key={i}
              >
                <div className="hardware-category-educational-info__content-top-container">
                  <SemiBdTitle5>{section?.label}</SemiBdTitle5>
                  <div className="hardware-category-educational-info__content-text-divider" />
                </div>
                <div className="hardware-category-educational-info__content-bottom-container">
                  <SemiBdTitle4>{section?.title}</SemiBdTitle4>
                  <RegBody>{section?.text}</RegBody>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </HardwareCategoryEducationalInfoStyled>
  )
}

export default HardwareCategoryEducationalInfo

const HardwareCategoryEducationalInfoStyled = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: var(--color-gray050);
  text-align: center;

  .hardware-category-educational-info__container {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: ${rem(343)};
    padding: ${rem(40)} 0 ${rem(40)};

    @media ${media.midUp} {
      max-width: ${rem(616)};
      padding: ${rem(72)} 0 ${rem(64)};
    }

    @media ${media.stMidUp} {
      max-width: ${rem(1072)};
      padding: ${rem(120)} 0;
    }

    .hardware-category-educational-info__img {
      width: ${rem(343)};
      height: ${rem(168)};
      border-radius: ${rem(8)};
      object-fit: cover;

      @media ${media.midUp} {
        width: 100%;
        height: ${rem(360)};
      }
    }

    .hardware-category-educational-info__title {
      padding-top: ${rem(32)};
      font-size: ${rem(32)};
      line-height: ${rem(40)};
      max-width: ${rem(339)};

      @media ${media.midUp} {
        max-width: ${rem(427)};
        padding-top: ${rem(40)};
      }

      @media ${media.stMidUp} {
        max-width: ${rem(675)};
        padding-top: ${rem(56)};
      }
    }

    .hardware-category-educational-info__text {
      padding-top: ${rem(16)};

      @media ${media.midUp} {
        max-width: ${rem(457)};
      }

      @media ${media.stMidUp} {
        padding-top: ${rem(24)};
      }
    }

    .hardware-category-educational-info__content-container {
      display: flex;
      flex-direction: column;
      gap: ${rem(24)};
      padding-top: ${rem(40)};
      text-align: left;

      @media ${media.midUp} {
        padding-top: ${rem(48)};
      }

      @media ${media.stMidUp} {
        padding-top: ${rem(56)};
      }

      .hardware-category-educational-info__content-inner-container {
        .hardware-category-educational-info__content-top-container {
          display: flex;
          gap: ${rem(24)};
          justify-content: space-between;
          align-items: space-between;

          h5 {
            font-size: ${rem(20)};
            line-height: ${rem(32)};
            color: var(--color-primary);

            @media ${media.midUp} {
              font-size: ${rem(24)};
              line-height: ${rem(32)};
            }
          }

          .hardware-category-educational-info__content-text-divider {
            flex: 1;
            border-bottom: ${rem(1)} solid var(--color-gray300);
            margin-bottom: ${rem(14)};
          }
        }

        .hardware-category-educational-info__content-bottom-container {
          @media ${media.stMidUp} {
            display: flex;
            gap: ${rem(40)};
            align-items: flex-start;
            justify-content: space-between;
          }
          h4 {
            padding-top: ${rem(8)};
            font-size: ${rem(24)};
            line-height: ${rem(32)};

            @media ${media.midUp} {
              padding-top: ${rem(16)};
              font-size: ${rem(32)};
              line-height: ${rem(40)};
            }
            @media ${media.stMidUp} {
              max-width: ${rem(309)};
            }
          }
          p {
            padding-top: ${rem(8)};

            @media ${media.midUp} {
              padding-top: ${rem(16)};
            }

            @media ${media.stMidUp} {
              max-width: ${rem(520)};
            }
          }
        }
      }
    }
  }
`
